// Turn a free-form description into a runnable AgentDraft.
// The "agent that builds the agent" is a strict-JSON call to the chat
// model. We then map the response onto the existing agents table shape.
import { supabase } from "@/integrations/supabase/client";
import type { AgentDraft } from "@/hooks/useAgents";
import { AGENT_TOOLS, type AgentToolId } from "@/lib/agentTools";

const VALID_TOOLS = new Set<AgentToolId>(AGENT_TOOLS.map((t) => t.id));

const SYSTEM = `You design AI agents. Given a one-line description, return ONLY a JSON object that matches:
{
  "name": string (2-40 chars),
  "description": string (one short sentence),
  "system_prompt": string (concrete instructions to the agent),
  "tools": string[] (subset of: ${AGENT_TOOLS.map((t) => `"${t.id}"`).join(", ")}),
  "default_goal": string (the first task to run when the user clicks "Run")
}
Rules:
- Pick only tools the agent genuinely needs.
- Keep the system prompt under 600 chars.
- Do not wrap the JSON in markdown fences.
- Return JSON only — no commentary.`;

/**
 * Call the chat model and coerce the response into a valid AgentDraft.
 * Falls back to a sensible default if the model misbehaves.
 */
export async function generateAgentSpec(description: string): Promise<AgentDraft> {
  const { data: { session } } = await supabase.auth.getSession();
  const apikey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-route`;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey,
      ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
    },
    body: JSON.stringify({
      prompt: `${SYSTEM}\n\nDescription: ${description}`,
      model: "google/gemini-3-flash-preview",
    }),
  });
  if (!res.ok || !res.body) {
    const t = await res.text().catch(() => "");
    throw new Error(t || `Spec generation failed (${res.status})`);
  }

  // ai-route streams SSE chunks — collect them all then parse.
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  let text = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const lines = buf.split("\n");
    buf = lines.pop() ?? "";
    for (const line of lines) {
      if (!line.startsWith("data:")) continue;
      const payload = line.slice(5).trim();
      if (!payload || payload === "[DONE]") continue;
      try {
        const evt = JSON.parse(payload);
        const delta = evt.choices?.[0]?.delta?.content;
        if (typeof delta === "string") text += delta;
      } catch {
        // skip non-JSON keepalives
      }
    }
  }

  return coerceDraft(text, description);
}

function coerceDraft(raw: string, fallbackDesc: string): AgentDraft {
  const json = extractJson(raw);
  const safeName = (json?.name ?? "New agent").toString().slice(0, 40);
  const safeDesc = (json?.description ?? fallbackDesc).toString().slice(0, 240);
  const safePrompt = (json?.system_prompt ?? `You are a helpful agent. ${fallbackDesc}`).toString().slice(0, 1200);
  const safeGoal = (json?.default_goal ?? fallbackDesc).toString().slice(0, 400);
  const tools = Array.isArray(json?.tools)
    ? (json!.tools.filter((t: unknown) => typeof t === "string" && VALID_TOOLS.has(t as AgentToolId)) as string[])
    : ["web_search"];

  return {
    name: safeName,
    description: safeDesc,
    system_prompt: safePrompt,
    tools,
    default_goal: safeGoal,
    brain_model: "google/gemini-3-flash-preview",
    visibility: "private",
  };
}

function extractJson(raw: string): any | null {
  if (!raw) return null;
  // Strip code fences if the model ignored instructions.
  const cleaned = raw.replace(/^```(?:json)?/i, "").replace(/```$/i, "").trim();
  const start = cleaned.indexOf("{");
  const end = cleaned.lastIndexOf("}");
  if (start === -1 || end === -1 || end <= start) return null;
  try {
    return JSON.parse(cleaned.slice(start, end + 1));
  } catch {
    return null;
  }
}